/**
 * message-source.ts — Chữ "ai / cái gì gửi" dưới tin gửi ra (chỉ nội bộ thấy, khách không thấy).
 * Nguồn do backend tính (`chat/message-origin.ts` → `message.source`).
 */
import type { ChatMessage, MessageSource, MessageSourceKind } from '@/hooks/use-conversations'
import { i18n } from '@/lib/i18n'

export type SourceIcon = 'staff' | 'ai' | 'automation' | 'outside' | 'zns' | 'unknown'

export interface SourceCaption {
  icon: SourceIcon
  /** Chữ ngắn cạnh giờ. */
  text: string
  /** Giải thích đầy đủ khi rê chuột. */
  title: string
}

/** Tin cũ từ bản backend chưa trả `source` → suy tối thiểu từ cột sẵn có (cùng thứ tự với backend). */
export function sourceOf(m: Pick<ChatMessage, 'source' | 'aiGenerated' | 'repliedBy' | 'contentType'>): MessageSource {
  if (m.source) return m.source
  if (m.contentType === 'zns_template') return { kind: 'zns', ref: null, label: null }
  if (m.aiGenerated) return { kind: 'ai', ref: null, label: null }
  if (m.repliedBy) return { kind: 'staff', ref: null, label: m.repliedBy.fullName ?? null }
  return { kind: 'unknown', ref: null, label: null }
}

/** `opts.defaultAiName`: tên nhân viên AI mặc định — thay chữ "AI chung" cho tin AI không ghi tên (tin cũ). */
export function sourceCaption(source: MessageSource, opts: { defaultAiName?: string | null } = {}): SourceCaption | null {
  const name = source.label?.trim() || null
  const kind: MessageSourceKind = source.kind
  switch (kind) {
    case 'contact':
    case 'system':
      return null
    case 'staff':
      return {
        icon: 'staff',
        text: name ?? i18n.t('convChat:source.staff'),
        title: name ? i18n.t('convChat:source.staffSentNamed', { name }) : i18n.t('convChat:source.staffSent'),
      }
    case 'ai_suggest': {
      const who = name ?? i18n.t('convChat:source.staff')
      return {
        icon: 'staff',
        text: i18n.t('convChat:source.aiSuggestText', { name: who }),
        title: i18n.t('convChat:source.aiSuggestTitle', { name: who }),
      }
    }
    case 'ai': {
      const defName = opts.defaultAiName?.trim()
      return {
        icon: 'ai',
        text: name ? i18n.t('convChat:source.aiNamed', { name }) : (defName || i18n.t('convChat:source.aiGeneral')),
        title: name
          ? i18n.t('convChat:source.aiTitleNamed', { name })
          : defName
            ? i18n.t('convChat:source.aiTitleDefault', { name: defName })
            : i18n.t('convChat:source.aiTitleGeneral'),
      }
    }
    case 'ai_external':
      return {
        icon: 'ai',
        text: name ? i18n.t('convChat:source.externalAgentNamed', { name }) : i18n.t('convChat:source.externalAgent'),
        title: i18n.t('convChat:source.externalAgentTitle'),
      }
    case 'automation':
      return {
        icon: 'automation',
        text: name ? i18n.t('convChat:source.automationNamed', { name }) : i18n.t('convChat:source.automation'),
        title: name ? i18n.t('convChat:source.automationTitleNamed', { name }) : i18n.t('convChat:source.automationTitle'),
      }
    case 'outside':
      return {
        icon: 'outside',
        text: i18n.t('convChat:source.outside'),
        title: i18n.t('convChat:source.outsideTitle'),
      }
    case 'zns':
      return {
        icon: 'zns',
        text: name ? i18n.t('convChat:source.znsNamed', { name }) : i18n.t('convChat:source.zns'),
        title: i18n.t('convChat:source.znsTitle'),
      }
    case 'unknown':
      return { icon: 'unknown', text: i18n.t('convChat:source.unknown'), title: i18n.t('convChat:source.unknownTitle') }
  }
}
